
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands, getDateRange } from "@common/src/utils/common";
import dayjs from "dayjs";
import BtnRadio from "@common/src/components/scope/btn-radio/index.vue";
import echarts from "echarts";
import t from "@common/src/i18n/t";
import appCode from "@common/src/utils/app-code";
import { addCurrencyUnit } from "@common/src/utils/tools";
import { InjectFunction } from "@common/src/utils/decorator";

const statement = namespace("statement");
const base = namespace("base");
const management = namespace("management");
const parts = namespace("parts");

@InjectFunction("permitApp")
@Component({
  components: { BtnRadio }
})
export default class Statement extends Vue {
  @statement.Action queryTireSalesAnalysis;
  @statement.Action queryTireSalesMonthAnalysis;
  @base.Action getDictionaryList;
  @parts.Action getPartsList;
  @management.Action getStoreList;
  addCurrencyUnit = addCurrencyUnit;
  dayjs = dayjs;
  splitThousands = splitThousands;
  emptyToLine = emptyToLine;
  searchForm: any = {
    storeCode: "", // 门店名称
    area: "", // 所在地区
    stateName: "", // 省级名称
    cityName: "", // 市级名称
    districtName: "", // 县级名称
    statisticMethod: "1", // 时间范围类型, 1-按日,2-按月
    beginDate: "", // 开始时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    endDate: "", // 结束时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    partsCode: ""
  };
  contentList: any = [];
  pieChartRadio: any = "0";
  barChartRadio: any = "0";
  get breadData() {
    return [
      { name: t("v210831.tire-sales-report"), path: "/statement/market-list" },
      { name: t("v210831.tire-sales-analysis") }
    ];
  }
  created() {
    this.init();
  }

  storeList: any = [];
  get searchList() {
    return [
      {
        label: t("v216_6.statistical-dimension"),
        type: "btn-radio",
        prop: "statisticMethod",
        options: this.dateTypeOptions,
        value: this.searchForm.statisticMethod,
        typeChangeMethod: this.handleDateTypeChange,
        required: true
      },
      {
        if: this.searchForm.statisticMethod === "1",
        label: t("v210831.time-frame"),
        value: [this.searchForm.beginDate, this.searchForm.endDate],
        resetValue: this.getDefaultDate("1"),
        type: "daterange",
        prop: "date",
        required: true,
        disabledFuture: true,
        maxDay: 90
      },
      {
        if: this.searchForm.statisticMethod === "2",
        label: t("v210831.time-frame"),
        value: [this.searchForm.beginDate, this.searchForm.endDate],
        resetValue: this.getDefaultDate("2"),
        type: "monthrange",
        prop: "month",
        required: true,
        disabledFuture: true,
        maxMonth: 12
      },
      {
        if: appCode === "speedbiz",
        label: t("parts.text"),
        type: "select-search",
        value: this.searchForm.partsCode,
        prop: "partsCode",
        selectList: this.partsList,
        searchFun: this.searchParts
      },
      {
        if: appCode === "speedbiz-operation",
        label: t("staff.store"),
        type: "select-search",
        value: this.searchForm.storeCode,
        prop: "storeCode",
        selectList: this.storeList,
        searchFun: this.searchStore,
        clearable: false
      },
      {
        if: appCode === "speedbiz-operation",
        label: t("setting.region"),
        type: "area",
        value: this.searchForm.area,
        labels: [],
        prop: "area",
        anyLevel: true,
        valueDistribute: ["stateName", "cityName", "districtName"]
      }
    ].filter(item => item.if !== false);
  }
  searchStore(val = "") {
    this.getStoreList({
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList ? { storeCode: this.routeParams.storeCode || "" } : { storeName: val })
    }).then(data => {
      this.storeList = (data.data?.list || []).map(item => {
        item.label = item.storeName;
        item.value = item.storeCode;
        return item;
      });
    });
  }
  routeParams: any = {};
  init() {
    this.routeParams = this.$route.params;
    if (this.routeParams.fromList === "1") {
      this.searchForm = this.routeParams;
    } else {
      this.handleDateTypeChange(this.searchForm.statisticMethod);
    }
    this.searchParts();

    if (appCode === "speedbiz-operation") {
      this.searchStore("");
    }
    delete this.searchForm.fromList;
    this.getChartData();
  }
  dataLastUpdateTime: any = "";
  getChartData() {
    /**
     * @refName
     *  piePayChart  饼图 实收金额
     *  pieOrderChart  饼图 订单金额
     *  pieNumberChart  饼图 台次
     *  barPayChart  柱状图 实收金额
     *  barOrderChart  柱状图 订单金额
     *  barNumberChart  柱状图 台次
     */

    this.queryTireSalesAnalysis(this.searchForm).then(data => {
      if (data.data.dataLastUpdateTime) {
        this.dataLastUpdateTime = data.data.dataLastUpdateTime;
      }
      let chartDatas = data.data;
      let piePayChartData = {
        data: [
          {
            name: t("v210831.jiatong-brand"),
            value: chartDatas.gitiActualAmount
          },
          {
            name: t("v210831.non-jiatong-brand"),
            value: chartDatas.otherActualAmount
          }
        ],
        total: chartDatas.actualAmount,
        text: addCurrencyUnit(t("v210831.total-paid-in-tire"), false, true),
        tip: t("v210831.paid-in-amount")
      };
      this.setChart(piePayChartData, "piePayChart", "pie");
      let pieOrderChartData = {
        data: [
          { name: t("v210831.jiatong-brand"), value: chartDatas.gitiAmount },
          {
            name: t("v210831.non-jiatong-brand"),
            value: chartDatas.otherAmount
          }
        ],
        total: chartDatas.orderAmount,
        text: addCurrencyUnit(t("v210831.total-amount-of"), false, true),
        tip: t("finance.order-amount")
      };
      this.setChart(pieOrderChartData, "pieOrderChart", "pie");
      let pieNumberChartData = {
        data: [
          { name: t("v210831.jiatong-brand"), value: chartDatas.gitiNum },
          { name: t("v210831.non-jiatong-brand"), value: chartDatas.otherNum }
        ],
        total: chartDatas.totalNum,
        text: t("v210831.total-tire-sales"),
        tip: t("setting.amount"),
        isNumber: true
      };
      this.setChart(pieNumberChartData, "pieNumberChart", "pie");
    });
    this.queryTireSalesMonthAnalysis(this.searchForm).then(data => {
      let chartDatas = data.data?.list || [];

      let list = chartDatas;

      let barPayChartData = {
        data1: list.map(v => v.gitiActualAmount),
        data2: list.map(v => v.otherActualAmount),
        tip: list,
        unit: this.$localMark.currency
      };
      this.setChart(barPayChartData, "barPayChart", "bar");
      let barOrderChartData = {
        data1: list.map(v => v.gitiAmount),
        data2: list.map(v => v.otherAmount),
        tip: list,
        unit: this.$localMark.currency
      };
      this.setChart(barOrderChartData, "barOrderChart", "bar");
      let barNumberChartData = {
        data1: list.map(v => v.gitiNum),
        data2: list.map(v => v.otherNum),
        tip: list,
        unit: t("other.tiao"),
        isNumber: true
      };
      this.setChart(barNumberChartData, "barNumberChart", "bar");
    });
  }
  setChart(data, refName, type) {
    console.log(89, data);
    let MyChart = echarts.init(this.$refs[refName]);
    let options = {};
    if (type === "pie") {
      // 饼图pie
      options = {
        color: ["#2278F5", "#00B478", "#FFC300"],
        tooltip: {
          trigger: "item",
          formatter: params => {
            return `${params.name}<br/>${data.tip}: ${
              data.isNumber ? params.value : this.$localMark.currencySymbol + splitThousands(params.value)
            }(${params.percent}%)`;
          }
        },
        legend: {
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        title: {
          show: true,
          text: data.text,
          subtext: splitThousands(data.total),
          top: "43%",
          left: "center",
          textStyle: {
            color: "rgba(0, 0, 0, 0.45)",
            fontWeight: 400,
            fontSize: 12
          },
          subtextStyle: {
            color: "rgba(0, 0, 0, 0.85)",
            fontWeight: 500,
            fontSize: 18
          }
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            label: {
              show: false
            },
            data: data.data
          }
        ]
      };
    } else if (type === "bar") {
      // 柱状图bar
      options = {
        color: ["#2278F5", "#00B478", "#FFC300"],
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          },
          formatter: params => {
            let str = `${data.tip[params[0].dataIndex].date}<br/>`;
            params.forEach(item => {
              str += `${item.marker}${item.seriesName}: ${
                Number(item.value)
                  ? data.isNumber
                    ? item.value
                    : this.$localMark.currencySymbol + splitThousands(item.value)
                  : "-"
              }<br/>`;
            });
            return str;
          }
        },
        legend: {
          data: [t("v210831.jiatong-brand"), t("v210831.non-jiatong-brand")],
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        xAxis: [
          {
            type: "category",
            axisTick: {
              alignWithLabel: true
            },
            data: data.tip.map(item => item.date),
            axisLabel: {
              rotate: 20 //倾斜度可以根据需求设置
            }
          }
        ],
        yAxis: [
          {
            name: t("base.unit") + ":" + data.unit,
            axisTick: { show: false },
            type: "value"
          }
        ],
        series: [
          {
            name: t("v210831.jiatong-brand"),
            type: "bar",
            barGap: 0,
            data: data.data1
          },
          {
            name: t("v210831.non-jiatong-brand"),
            type: "bar",
            data: data.data2
          }
        ]
      };
    }
    MyChart.setOption(options);
    if (type === "pie") {
      const getSelectedTotal = selected => {
        return data.data.reduce((total, item) => {
          if (selected && selected[item.name] === false) {
            return total;
          }
          const value = Number(item.value || 0);
          return total + (isNaN(value) ? 0 : value);
        }, 0);
      };
      MyChart.off("legendselectchanged");
      MyChart.on("legendselectchanged", params => {
        MyChart.setOption({
          title: {
            subtext: splitThousands(getSelectedTotal(params.selected))
          }
        });
      });
    }
    window.addEventListener("resize", () => {
      MyChart.resize();
    });
  }
  handlePieChange(value) {
    this.pieChartRadio = value;
  }
  handleBarChange(value) {
    this.barChartRadio = value;
  }
  handleSearch(data) {
    this.mix_pageNum = 1;
    data.forEach(item => {
      if (item.prop) {
        if (item.prop === "area") {
          this.searchForm.stateName = item.labels[0] || "";
          this.searchForm.cityName = item.labels[1] || "";
          this.searchForm.districtName = item.labels[2] || "";
        } else if (item.prop === "date" || item.prop === "month") {
          this.searchForm.beginDate = item.value?.[0];
          this.searchForm.endDate = item.value?.[1];
        } else {
          this.searchForm[item.valueAlias || item.prop] = item.value;
        }
      }
    });
    if (this.permitApp("speedbiz-operation") && !this.searchForm.storeCode) {
      return this.$message.error(t("v210831.please-select-a-store"));
    }
    this.getChartData();
  }
  dateTypeOptions = [
    { label: t("sb_copy.query-by-day"), value: "1" },
    { label: t("sb_copy.query-by-month"), value: "2" }
  ];
  handleDateTypeChange(val) {
    [this.searchForm.beginDate, this.searchForm.endDate] = this.getDefaultDate(val);
    this.searchForm.statisticMethod = val;
  }
  getDefaultDate(type: "1" | "2"): Array<string> {
    if (type === "1") {
      return getDateRange("first_day_to_today_of_current_month", "YYYYMMDD");
    } else if (type === "2") {
      return getDateRange("first_month_to_current_month_of_current_year", "YYYYMM");
    } else {
      return [];
    }
  }
  partsList = [];
  searchParts(val = "") {
    this.getPartsList({
      status: "1",
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList && this.routeParams.partsCode
        ? { partsCodeList: [this.routeParams.partsCode || ""] }
        : { partsName: val })
    }).then(data => {
      this.partsList = (data.data?.list || []).map(item => {
        item.label = item.partsName;
        item.value = item.partsCode;
        return item;
      });
    });
  }
}
